// 5-state meeting status pill. Class strings are pinned by brand
// SKILL.md §"Meeting status pills" — DO NOT diverge or downstream
// surfaces (summary cards, filter chips, table cells) will lose the
// shared visual vocabulary. Renders the textual label plus a coloured
// dot indicator (aria-hidden so screen readers only read the label).

import { clsx } from "clsx";
import type { MeetingDerivedStatus } from "../../../../types";

export interface MeetingStatusPillProps {
  status: MeetingDerivedStatus;
  className?: string;
}

interface PillSpec {
  label: string;
  pillClass: string;
  dotClass: string;
}

const PILL_SPECS: Record<MeetingDerivedStatus, PillSpec> = {
  scheduled: {
    label: "Scheduled",
    pillClass:
      "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dotClass: "bg-slate-400 dark:bg-slate-500",
  },
  "due-soon": {
    label: "Due soon",
    pillClass:
      "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400",
    dotClass: "bg-brand-500",
  },
  overdue: {
    label: "Overdue",
    pillClass:
      "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    dotClass: "bg-red-500",
  },
  held: {
    label: "Held",
    pillClass:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    dotClass: "bg-emerald-500",
  },
  cancelled: {
    label: "Cancelled",
    pillClass:
      "bg-slate-100 text-slate-400 dark:bg-slate-700/50 dark:text-slate-500",
    dotClass: "bg-slate-300 dark:bg-slate-600",
  },
};

export default function MeetingStatusPill({
  status,
  className,
}: MeetingStatusPillProps) {
  const spec = PILL_SPECS[status];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-medium",
        spec.pillClass,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={clsx("h-1.5 w-1.5 rounded-full", spec.dotClass)}
      />
      {spec.label}
    </span>
  );
}
